<template>
  <div class="product-view">
    <MarxentNotification />
    <Alerts :product-id="productId" :alert-props="alertProps" />
    <component :is="ProductTypeComponent" />
    <component
      :is="ModalTypeComponent"
      v-if="ModalTypeComponent"
      :key="activeModal"
      v-bind="modalProps"
      page-type="pdp"
      v-on="modalEvtHandlers"
      @error-alert="onErrorAlert"
    />
  </div>
</template>

<script setup lang="ts">
import { provide, computed, type Component } from 'vue';

import StandardProduct from '~/components/ProductCard/StandardProduct.vue';
import GiftCardProduct from '~/components/ProductCard/GiftCardProduct.vue';
import GiftWithPurchase from '~/components/ProductCard/GiftWithPurchase.vue';
import MasterProduct from '~/components/ProductCard/master/MasterProduct.vue';
import MasterShopTheCollectionProduct from '~/components/ProductCard/master/MasterShopTheCollectionProduct.vue';
import UnavailableProduct from '~/components/ProductCard/UnavailableProduct.vue';
import ConsolidatedLatchKeyProduct from '~/components/ProductCard/ConsolidatedLatchKeyProduct.vue';
import ConsolidatedStandard from '~/components/ProductCard/ConsolidatedStandard.vue';
import MarxentNotification from '~/modules/marxent/components/MarxentNotification.vue';
import Alerts from '~/modules/alerts/components/AlertsView.vue';

import { useAlertsProps } from '~/modules/alerts/composables/useAlertProps';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useModal } from '~/composables/utils/useModal';
import { useProductType } from '~/composables/useProductType';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';

import UNLOCK_SCROLL from './constants';
import unlockScroll from './utils';

const { productId } = useProduct();
const { type } = useProductType();
const { isShopTheCollectionExperimentEnabled } = useFeatureEligibility();
const { activeModal, modalProps, modalEvtHandlers, ModalTypeComponent } = useModal();

const MasterProductComponent = isShopTheCollectionExperimentEnabled ? MasterShopTheCollectionProduct : MasterProduct;

const ProductTypeComponents: Record<typeof type, Component> = {
  giftCard: GiftCardProduct,
  shopTheSet: StandardProduct,
  master: MasterProductComponent,
  giftWithPurchase: GiftWithPurchase,
  unavailable: UnavailableProduct,
  latchKey: ConsolidatedLatchKeyProduct,
  consolidatedStandard: ConsolidatedStandard,
  consolidatedMaster: StandardProduct,
  consolidatedLatchKey: ConsolidatedLatchKeyProduct,
  standard: StandardProduct,
};

const ProductTypeComponent = computed(() => ProductTypeComponents[type]);

const { onErrorAlert, alertProps } = useAlertsProps();

// Provide unlockScroll function for external dependencies that
// don't trigger 'close' via useModal inside of pdp
provide(UNLOCK_SCROLL, unlockScroll);
</script>

<style lang="scss" scoped>
.product-view {
  flex: 1;
  max-width: 100%;
}
</style>
